import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, set, push, update } from 'firebase/database';
import './Payment.css';
// Import logo
import payLogo from '../img/pay.png';

const Payment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [qrImage, setQrImage] = useState('');
  const [upiId, setUpiId] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [utrInput, setUtrInput] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [orderTotal, setOrderTotal] = useState(0);
  const [timer, setTimer] = useState(300);
  const [showUtrInput, setShowUtrInput] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // NEW: State for payment gateway
  const [paymentGateway, setPaymentGateway] = useState('pg1');
  const [pgConfigs, setPgConfigs] = useState({
    pg1: { qrImage: '', upiId: '' },
    pg2: { qrImage: '', upiId: '' },
    pg3: { qrImage: '', upiId: '' }
  });
  
  const pollTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const redirectTimerRef = useRef(null);

  // Detect device
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(navigator.userAgent);

  useEffect(() => {
    // Get current user
    const user = localStorage.getItem('currentUser');
    if (user) {
      try {
        setCurrentUser(JSON.parse(user));
      } catch (e) {
        setCurrentUser(null);
      }
    }

    // Get cart items from localStorage
    const cartItemsData = JSON.parse(localStorage.getItem('cartItems') || '[]');
    setCartItems(cartItemsData);
    const total = cartItemsData.reduce(function(sum, item) { return sum + item.total; }, 0);
    setOrderTotal(total);
    
    const fetchPaymentConfig = async function() {
      try {
        const db = getDatabase();
        
        // Get payment mode from localStorage (set in Cart page)
        const savedPaymentMode = localStorage.getItem('paymentMode') || '1';
        const pgKey = 'pg' + savedPaymentMode;
        setPaymentGateway(pgKey);
        
        // Try to get from admin/settings first
        let snapshot = await get(ref(db, 'admin/settings'));
        let configData = null;
        
        if (snapshot.exists()) {
          configData = snapshot.val();
        } else {
          // Try paymentConfig as fallback
          const paymentSnapshot = await get(ref(db, 'paymentConfig'));
          if (paymentSnapshot.exists()) {
            configData = paymentSnapshot.val();
          }
        }
        
        if (configData) {
          // Store PG configurations
          var pgConfigsData = {
            pg1: configData.pg1 || { qrImage: '', upiId: '', merchantName: '' },
            pg2: configData.pg2 || { qrImage: '', upiId: '', merchantName: '' },
            pg3: configData.pg3 || { qrImage: '', upiId: '', merchantName: '' }
          };
          setPgConfigs(pgConfigsData);
          
          // Get the selected PG details
          var selectedPG = pgConfigsData[pgKey] || {};
          
          // Set UPI ID from selected PG (required)
          if (selectedPG.upiId) {
            setUpiId(selectedPG.upiId);
          } else {
            setError('UPI ID not configured for ' + pgKey.toUpperCase());
            setLoading(false);
            return;
          }
          
          // Set QR image from selected PG (required)
          if (selectedPG.qrImage) {
            setQrImage(selectedPG.qrImage);
          } else {
            setError('QR Code not configured for ' + pgKey.toUpperCase());
            setLoading(false);
            return;
          }
          
          // Set merchant name (use PG specific if available, else global)
          var merchantName = selectedPG.merchantName || configData.merchantName || 'InfoNaukri';
          setMerchantName(merchantName);
          
          setTransactionId('ORD' + Date.now() + Math.floor(Math.random() * 9000 + 1000));
          setError('');
        } else {
          setError('Payment configuration not found. Please contact admin.');
        }
      } catch (error) {
        console.error('Error fetching payment config:', error);
        setError('Failed to load payment details.');
      }
      setLoading(false);
    };

    fetchPaymentConfig();

    return function() {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
    };
  }, []);

  // Start countdown timer when QR is shown
  useEffect(function() {
    if (!loading && qrImage && upiId) {
      setTimer(300);
      if (countdownRef.current) clearInterval(countdownRef.current);
      
      countdownRef.current = setInterval(function() {
        setTimer(function(prev) {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            setPaymentStatus({ 
              type: 'timeout', 
              message: 'QR code expired. Please refresh.'
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return function() {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [loading, qrImage, upiId]);

  var formatTime = function(seconds) {
    var mins = Math.floor(seconds / 60);
    var secs = seconds % 60;
    return mins.toString().padStart(2, '0') + ':' + secs.toString().padStart(2, '0');
  };

  // GENERATE QR CODE - Medium size
  var generateQRCode = function() {
    if (!upiId) return '';
    var upiLink = 'upi://pay?pa=' + encodeURIComponent(upiId) + '&pn=' + encodeURIComponent(merchantName || '') + '&am=' + encodeURIComponent(orderTotal) + '&cu=INR&tn=' + encodeURIComponent(transactionId);
    return 'https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=15&data=' + encodeURIComponent(upiLink);
  };

  var buildPhonePeLink = function() {
    if (!upiId) return '';
    var amount = orderTotal;
    var txnId = transactionId;
    
    if (isAndroid) {
      return 'phonepe://native?data=' + encodeURIComponent(btoa(JSON.stringify({
        contact: { vpa: upiId, type: 'VPA' },
        p2pPaymentCheckoutParams: {
          note: txnId,
          initialAmount: Math.round(amount * 100),
          currency: 'INR',
          checkoutType: 'DEFAULT',
          disableAmountEdit: true
        }
      }))) + '&id=p2ppayment';
    } else if (isIOS) {
      return 'https://phone.pe/pay?pa=' + encodeURIComponent(upiId) + '&pn=' + encodeURIComponent(merchantName || '') + '&am=' + encodeURIComponent(amount) + '&cu=INR&tn=' + encodeURIComponent(txnId);
    } else {
      return 'upi://pay?pa=' + encodeURIComponent(upiId) + '&pn=' + encodeURIComponent(merchantName || '') + '&am=' + encodeURIComponent(amount) + '&cu=INR&tn=' + encodeURIComponent(txnId);
    }
  };

  var buildPaytmLink = function() {
    if (!upiId) return '';
    var amount = orderTotal;
    var txnId = transactionId;
    
    if (isAndroid) {
      return 'paytmmp://cash_wallet?pa=' + encodeURIComponent(upiId) + '&pn=' + encodeURIComponent(merchantName || '') + '&am=' + encodeURIComponent(amount) + '&cu=INR&tn=' + encodeURIComponent(txnId) + '&tr=' + encodeURIComponent(txnId);
    } else if (isIOS) {
      return 'https://paytm.com/pay?pa=' + encodeURIComponent(upiId) + '&pn=' + encodeURIComponent(merchantName || '') + '&am=' + encodeURIComponent(amount) + '&cu=INR&tn=' + encodeURIComponent(txnId);
    } else {
      return 'upi://pay?pa=' + encodeURIComponent(upiId) + '&pn=' + encodeURIComponent(merchantName || '') + '&am=' + encodeURIComponent(amount) + '&cu=INR&tn=' + encodeURIComponent(txnId);
    }
  };

  var handlePhonePePayment = function() {
    if (isProcessing) return;
    setIsProcessing(true);
    setPaymentStatus(null);
    
    var paymentLink = buildPhonePeLink();
    if (paymentLink) {
      window.location.href = paymentLink;
    }
    startPolling();
  };

  var handlePaytmPayment = function() {
    if (isProcessing) return;
    setIsProcessing(true);
    setPaymentStatus(null);
    
    var paymentLink = buildPaytmLink();
    if (paymentLink) {
      window.location.href = paymentLink;
    }
    startPolling();
  };

  var startPolling = function() {
    var attempts = 0;
    var maxAttempts = 20;
    
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    
    setPaymentStatus({ type: 'pending', message: 'Processing payment...' });
    
    pollTimerRef.current = setInterval(function() {
      attempts++;
      
      if (attempts >= maxAttempts) {
        clearInterval(pollTimerRef.current);
        setPaymentStatus({ 
          type: 'timeout', 
          message: 'Payment timeout. Please try again.',
          showUtrInput: false
        });
        setIsProcessing(false);
        return;
      }
      
      simulateVerification(attempts);
    }, 3000);
  };

  var simulateVerification = function(attempt) {
    if (attempt > 5 && Math.random() < 0.3) {
      clearInterval(pollTimerRef.current);
      var utr = 'UTR' + Date.now().toString().slice(-8);
      setPaymentStatus({ 
        type: 'success', 
        message: 'Payment successful!',
        utr: utr
      });
      setIsProcessing(false);
      
      // Save order to Firebase
      saveOrderToFirebase(utr, 'completed');
    } else if (attempt > 3 && Math.random() < 0.1) {
      clearInterval(pollTimerRef.current);
      setPaymentStatus({ 
        type: 'failed', 
        message: 'Payment failed. Please try again.',
        showUtrInput: false
      });
      setIsProcessing(false);
    }
  };

  // Clear cart and update badge
  var clearCartAndUpdateBadge = function() {
    // Clear cart items from localStorage
    localStorage.setItem('cartItems', JSON.stringify([]));
    
    // Clear guest cart if exists
    var guestId = localStorage.getItem('guestUserId');
    if (guestId) {
      localStorage.setItem('cart_' + guestId, JSON.stringify([]));
    }
    
    // Dispatch event to update header badge
    var cartUpdateEvent = new CustomEvent('cartUpdated', { 
      detail: { 
        userId: currentUser ? currentUser.userId : null,
        isLoggedIn: !!currentUser,
        cartData: [] 
      } 
    });
    window.dispatchEvent(cartUpdateEvent);
  };

  // Start redirect countdown
  var startRedirectCountdown = function() {
    setRedirectCountdown(5);
    if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
    
    redirectTimerRef.current = setInterval(function() {
      setRedirectCountdown(function(prev) {
        if (prev <= 1) {
          clearInterval(redirectTimerRef.current);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Save order to Firebase with unique ID - IMPROVED
  var saveOrderToFirebase = async function(utr, status) {
    setSavingOrder(true);
    try {
      var db = getDatabase();
      
      // Get user ID - ensure we have a valid ID
      var userId = currentUser ? currentUser.userId : null;
      if (!userId) {
        // Try to get from localStorage
        var user = localStorage.getItem('currentUser');
        if (user) {
          try {
            var parsedUser = JSON.parse(user);
            userId = parsedUser.userId || 'guest';
          } catch (e) {
            userId = 'guest';
          }
        } else {
          userId = 'guest';
        }
      }
      
      // Generate unique order ID
      var orderId = generateOrderId();
      
      // Get referral code from localStorage if exists
      var referralCode = localStorage.getItem('referenceCode') || '';
      var referralUserId = localStorage.getItem('referralUserId') || '';
      var referralIsUniversal = localStorage.getItem('referralIsUniversal') === 'true';
      
      // Get payment gateway
      var paymentGatewaySelected = localStorage.getItem('paymentMode') || '1';
      
      // Prepare order data with all details
      var orderData = {
        orderId: orderId,
        userId: userId,
        userName: currentUser && currentUser.firstName ? currentUser.firstName + ' ' + (currentUser.lastName || '') : 'Guest User',
        userEmail: currentUser ? currentUser.email || 'guest@email.com' : 'guest@email.com',
        userPhone: currentUser ? currentUser.phone || 'N/A' : 'N/A',
        items: cartItems.map(function(item) {
          return {
            id: item.id,
            name: item.name,
            price: item.price,
            gst: item.gst,
            total: item.total,
            category: item.category || 'General'
          };
        }),
        totalAmount: orderTotal,
        utr: utr,
        transactionId: transactionId,
        status: status || 'completed',
        paymentMethod: 'UPI',
        paymentGateway: paymentGatewaySelected,
        referralCode: referralCode,
        referralUserId: referralUserId,
        referralIsUniversal: referralIsUniversal,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('📦 Saving order with data:', orderData);
      console.log('👤 User ID:', userId);
      console.log('📋 Order ID:', orderId);

      // Save to orders/{userId}/{orderId}
      await set(ref(db, 'orders/' + userId + '/' + orderId), orderData);
      console.log('✅ Order saved to orders/' + userId + '/' + orderId);
      
      // Also save to allOrders for admin view
      await set(ref(db, 'allOrders/' + orderId), orderData);
      console.log('✅ Order saved to allOrders/' + orderId);

      // Update user's order history
      var userOrdersRef = ref(db, 'userOrders/' + userId);
      var userOrdersSnapshot = await get(userOrdersRef);
      var existingOrders = [];
      if (userOrdersSnapshot.exists()) {
        existingOrders = userOrdersSnapshot.val() || [];
      }
      existingOrders.push(orderId);
      await set(userOrdersRef, existingOrders);
      console.log('✅ User order history updated');

      // Dispatch event to update header badge
      clearCartAndUpdateBadge();
      
      // Start redirect countdown
      startRedirectCountdown();
      
      setSavingOrder(false);
      return true;
      
    } catch (error) {
      console.error('❌ Error saving order:', error);
      setSavingOrder(false);
      setPaymentStatus({ 
        type: 'error', 
        message: 'Order saved but there was an error. Please contact support.',
        showUtrInput: false
      });
      return false;
    }
  };

  // Generate unique order ID
  var generateOrderId = function() {
    var timestamp = Date.now().toString(36);
    var random = Math.random().toString(36).substr(2, 6);
    return 'ORD_' + timestamp + '_' + random.toUpperCase();
  };

  var resetPayment = function() {
    setPaymentStatus(null);
    setIsProcessing(false);
    setUtrInput('');
    setShowUtrInput(false);
    setRedirectCountdown(3);
    if (redirectTimerRef.current) {
      clearInterval(redirectTimerRef.current);
    }
    setTransactionId('ORD' + Date.now() + Math.floor(Math.random() * 9000 + 1000));
    setTimer(300);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = setInterval(function() {
        setTimer(function(prev) {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  if (loading) {
    return React.createElement(
      'div',
      { className: 'payment-loading', style: { height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' } },
      React.createElement('div', { className: 'spinner' }),
      React.createElement('p', null, 'Loading payment details...')
    );
  }

  // Check if data is loaded from Firebase
  if (!upiId || error) {
    return React.createElement(
      'div',
      { className: 'payment-error', style: { height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' } },
      React.createElement(
        'div',
        { className: 'container' },
        React.createElement(
          'div',
          { className: 'error-card', style: { textAlign: 'center', padding: '40px' } },
          React.createElement('i', { className: 'fas fa-exclamation-circle', style: { fontSize: '48px', color: '#dc2626' } }),
          React.createElement('h2', null, 'Payment Configuration Missing'),
          React.createElement('p', null, error || 'Please contact admin to configure UPI payment settings.'),
          React.createElement(
            'button',
            { 
              className: 'btn-primary',
              onClick: function() { navigate('/'); }
            },
            'Go Back Home'
          )
        )
      )
    );
  }

  // Get the current PG name for display
  var pgDisplayName = paymentGateway.toUpperCase();

  return React.createElement(
    'div',
    { 
      className: 'payment-page',
      style: { 
        height: '100vh', 
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1a3a5c 50%, #0f172a 100%)',
        padding: '8px',
        boxSizing: 'border-box'
      }
    },
    React.createElement(
      'div',
      { 
        className: 'payment-card',
        style: {
          maxWidth: '420px',
          width: '100%',
          height: '98vh',
          maxHeight: '780px',
          background: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '12px 16px',
          boxSizing: 'border-box'
        }
      },
      
      // === HEADER / BRANDING - WHITE TEXT ===
      React.createElement(
        'div',
        { 
          className: 'payment-branding',
          style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '6px 0 2px 0',
            flexShrink: 0,
            background: 'linear-gradient(135deg, #0f172a, #1a3a5c)',
            margin: '-12px -16px 0 -16px',
            padding: '10px 16px 6px 16px',
            borderBottom: '2px solid #65d2bc'
          }
        },
        // Logo
        React.createElement(
          'div',
          { 
            className: 'brand-logo-circle',
            style: {
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #65d2bc, #1a3a5c)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              padding: '2px',
              marginBottom: '2px',
              border: '2px solid #65d2bc'
            }
          },
          React.createElement('img', {
            src: payLogo,
            alt: 'InfoNaukri',
            className: 'brand-logo-img',
            style: {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '50%'
            }
          })
        ),
        // Brand Name - White and Big
        React.createElement(
          'div',
          { 
            className: 'brand-name',
            style: {
              fontSize: '20px',
              fontWeight: '800',
              color: '#ffffff',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              letterSpacing: '0.5px'
            }
          },
          merchantName || 'InfoNaukri'
        ),
        // Info Edge - White
        React.createElement(
          'div',
          { 
            className: 'brand-info-edge',
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '10px',
              color: '#ffffff',
              fontWeight: '500',
              letterSpacing: '0.5px',
              marginTop: '1px',
              opacity: 0.8
            }
          },
          React.createElement('span', { className: 'dash', style: { width: '16px', height: '1.5px', background: '#65d2bc' } }),
          React.createElement('span', { className: 'services-text' }, 'INFO EDGE'),
          React.createElement('span', { className: 'dash', style: { width: '16px', height: '1.5px', background: '#65d2bc' } })
        ),
      ),

      // === MAIN CONTENT AREA ===
      React.createElement(
        'div',
        { 
          className: 'payment-content',
          style: {
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            padding: '4px 0'
          }
        },
        
        // Saving indicator
        savingOrder && React.createElement(
          'div',
          { 
            className: 'saving-indicator',
            style: { 
              textAlign: 'center', 
              padding: '4px', 
              background: '#e8f4fd', 
              borderRadius: '6px', 
              fontSize: '12px',
              flexShrink: 0
            } 
          },
          React.createElement('i', { className: 'fas fa-spinner fa-spin' }),
          ' Saving order...'
        ),
        
        // Amount
        React.createElement(
          'div',
          { 
            className: 'amount-display',
            style: {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 10px',
              background: '#f8fafc',
              borderRadius: '10px',
              flexShrink: 0
            }
          },
          React.createElement('span', { className: 'amount-label', style: { fontSize: '13px', fontWeight: '500', color: '#64748b' } }, 'Total Amount'),
          React.createElement('span', { className: 'amount-value', style: { fontSize: '18px', fontWeight: '700', color: '#1a3a5c' } }, '₹' + orderTotal.toFixed(2))
        ),
        
        // Timer
        React.createElement(
          'div',
          { 
            className: 'timer-display',
            style: {
              textAlign: 'center',
              fontSize: '12px',
              color: timer <= 60 ? '#dc2626' : '#64748b',
              fontWeight: '600',
              flexShrink: 0
            }
          },
          React.createElement('i', { className: 'fas fa-clock' }),
          ' ',
          formatTime(timer),
          timer <= 60 && React.createElement('span', { className: 'timer-warning', style: { marginLeft: '6px' } }, '⚠️ Expiring soon!')
        ),
        
        // QR Code - Medium size (between small and original)
        React.createElement(
          'div',
          { 
            className: 'qr-container',
            style: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4px 0',
              flex: '0 0 auto',
              minHeight: '220px',
              maxHeight: '260px'
            }
          },
          React.createElement(
            'div',
            { 
              className: 'qr-wrapper',
              style: {
                background: '#ffffff',
                padding: '8px',
                borderRadius: '14px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                display: 'inline-block',
                border: '2px solid #e2e8f0',
                width: '220px',
                height: '220px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }
            },
            React.createElement('img', { 
              src: qrImage || generateQRCode(),
              alt: 'UPI QR Code - ' + pgDisplayName,
              className: 'qr-image',
              style: {
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'block',
                borderRadius: '8px',
                background: '#ffffff',
                objectFit: 'contain'
              },
              onError: function(e) {
                e.target.src = generateQRCode();
              }
            })
          )
        ),
        
        // UPI ID - Compact with copy
        React.createElement(
          'div',
          { 
            className: 'upi-display',
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '4px 0',
              flexShrink: 0
            }
          },
          React.createElement('span', { className: 'upi-label', style: { fontSize: '11px', fontWeight: '500', color: '#64748b' } }, 'UPI:'),
          React.createElement('span', { className: 'upi-value', style: { fontSize: '12px', fontWeight: '600', color: '#1a3a5c' } }, upiId),
          React.createElement(
            'button',
            {
              onClick: function() {
                navigator.clipboard.writeText(upiId);
                alert('UPI ID copied!');
              },
              style: {
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#65d2bc',
                fontSize: '13px',
                padding: '2px 6px',
                borderRadius: '4px'
              }
            },
            React.createElement('i', { className: 'fas fa-copy' })
          )
        ),
        
        // Transaction ID - Compact
        React.createElement(
          'div',
          { 
            className: 'transaction-display',
            style: {
              display: 'flex',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '10px',
              color: '#94a3b8',
              flexShrink: 0
            }
          },
          React.createElement('span', { className: 'txn-label' }, 'Txn ID:'),
          React.createElement('span', { className: 'txn-value', style: { fontWeight: '500', color: '#64748b' } }, transactionId)
        ),
        
        // Payment Status - Compact
        paymentStatus && React.createElement(
          'div',
          { 
            className: 'status-banner ' + paymentStatus.type,
            style: {
              padding: '6px 10px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '500',
              textAlign: 'center',
              flexShrink: 0,
              background: paymentStatus.type === 'success' ? '#d1fae5' : 
                        paymentStatus.type === 'failed' ? '#fee2e2' : 
                        paymentStatus.type === 'pending' ? '#fef3c7' : '#fef3c7',
              color: paymentStatus.type === 'success' ? '#065f46' : 
                     paymentStatus.type === 'failed' ? '#991b1b' : '#92400e'
            }
          },
          React.createElement('span', { className: 'status-icon' }, 
            paymentStatus.type === 'success' ? '✅' : 
            paymentStatus.type === 'failed' ? '❌' : 
            paymentStatus.type === 'pending' ? '⏳' : '⏱️'
          ),
          React.createElement('span', { className: 'status-message', style: { marginLeft: '4px' } }, paymentStatus.message),
          paymentStatus.utr && React.createElement('span', { className: 'status-utr', style: { display: 'block', fontSize: '10px', marginTop: '2px', fontWeight: '600' } }, 'UTR: ' + paymentStatus.utr),
          paymentStatus.type === 'success' && redirectCountdown > 0 && React.createElement(
            'span',
            { className: 'redirect-countdown', style: { display: 'block', marginTop: '2px', fontSize: '11px', fontWeight: '600' } },
            'Redirecting in ' + redirectCountdown + '...'
          ),
          (paymentStatus.type === 'failed' || paymentStatus.type === 'timeout' || paymentStatus.type === 'error') && 
            React.createElement(
              'button',
              { 
                className: 'btn-retry', 
                onClick: resetPayment,
                style: {
                  marginTop: '4px',
                  padding: '4px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  cursor: 'pointer'
                }
              },
              'Try Again'
            ),
          paymentStatus.type === 'success' && 
            React.createElement(
              'button',
              { 
                className: 'btn-home', 
                onClick: function() { navigate('/'); },
                style: {
                  marginTop: '4px',
                  padding: '4px 16px',
                  background: '#65d2bc',
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }
              },
              'Go Home'
            )
        ),
        
        // Divider + App Buttons - Compact
        (!paymentStatus || paymentStatus.type !== 'success') && React.createElement(
          React.Fragment,
          null,
          React.createElement(
            'div',
            { 
              className: 'divider',
              style: {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 0',
                flexShrink: 0
              }
            },
            React.createElement('hr', { style: { flex: 1, border: 'none', borderTop: '1px solid #e2e8f0' } }),
            React.createElement('span', { style: { fontSize: '10px', color: '#94a3b8', fontWeight: '500', whiteSpace: 'nowrap' } }, 'Pay with UPI App'),
            React.createElement('hr', { style: { flex: 1, border: 'none', borderTop: '1px solid #e2e8f0' } })
          ),
          React.createElement(
            'div',
            { 
              className: 'app-buttons',
              style: {
                display: 'flex',
                gap: '8px',
                flexShrink: 0,
                paddingBottom: '4px'
              }
            },
            React.createElement(
              'button',
              { 
                className: 'btn-phonepe',
                onClick: handlePhonePePayment,
                disabled: isProcessing || savingOrder,
                style: {
                  flex: 1,
                  padding: '8px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #5f259f, #7c3aed)',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }
              },
              React.createElement('i', { className: 'fas fa-mobile-alt' }),
              ' PhonePe',
              isProcessing && React.createElement('span', { className: 'spinner-small' })
            ),
            React.createElement(
              'button',
              { 
                className: 'btn-paytm',
                onClick: handlePaytmPayment,
                disabled: isProcessing || savingOrder,
                style: {
                  flex: 1,
                  padding: '8px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #00baf2, #003b70)',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }
              },
              React.createElement('i', { className: 'fas fa-mobile-alt' }),
              ' Paytm',
              isProcessing && React.createElement('span', { className: 'spinner-small' })
            )
          )
        ),
        
        // Secure Badge - Always at bottom
        React.createElement(
          'div',
          { 
            className: 'secure-badge',
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '4px 0',
              fontSize: '10px',
              color: '#94a3b8',
              flexShrink: 0,
              borderTop: '1px solid #f1f5f9',
              marginTop: 'auto'
            }
          },
          React.createElement('span', null, '🔒'),
          React.createElement('span', null, 'Secure Payment'),
          React.createElement('span', null, '•'),
          React.createElement('span', null, 'UPI Verified')
        )
      )
    )
  );
};

export default Payment;